import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  Exercise,
  PlanDocumentUtils,
  PlannedTier,
  rpeOptions,
  Workout,
  WorkoutType,
} from 'fitforce-document-sync';
import { cloneDeep, isInteger } from 'lodash';

import { DocumentService } from 'app/document/services/document.service';
import { EventBusService } from 'app/document/services/event-bus.service';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ExerciseCategory } from '../../document-planning/document-planning.component';

@Component({
  selector: 'document-day-planning',
  templateUrl: './document-day-planning.component.html',
  styleUrls: ['./document-day-planning.component.scss'],
  /**
   * ChangeDetectionStrategy.OnPush: Use the CheckOnce strategy, meaning that automatic change
   * detection is deactivated until reactivated by setting the strategy to Default (CheckAlways).
   * Change detection can still be explicitly invoked. This strategy applies to all child
   * directives and cannot be overridden.
   */
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentDayPlanningComponent
  implements OnChanges, OnInit, OnDestroy {
  form: FormGroup;
  selectedTier: PlannedTier;
  workoutName: FormControl;
  workoutCompletionTime: FormControl;
  workoutEstimatedRPE: FormControl;
  rpeOptions = rpeOptions;
  canMoveTiers: boolean;
  templateWorkoutTypes: WorkoutType[];

  @Input() readonly canEdit: boolean;
  @Input() readonly workout: Workout;
  @Input() readonly workoutType?: WorkoutType;
  @Input() readonly exercises: Exercise[];
  @Input() readonly exerciseCategories: ExerciseCategory[];
  @Input() readonly planMetadata: any;

  @Output() addedTierToLibrary: EventEmitter<PlannedTier> =
    new EventEmitter<PlannedTier>();
  @Output() addedTierFromLibrary: EventEmitter<{
    workoutId: string;
    tiers: PlannedTier[];
  }> = new EventEmitter<{
    workoutId: string;
    tiers: PlannedTier[];
  }>();
  @Output() viewSwitched: EventEmitter<void> = new EventEmitter<void>();
  @Output() tierUpdated: EventEmitter<{
    workoutId: string;
    index: number;
    tier: PlannedTier;
  }> = new EventEmitter<{
    workoutId: string;
    index: number;
    tier: PlannedTier;
  }>();
  @Output() tiersUpdated: EventEmitter<{
    workoutId: string;
    tiers: PlannedTier[];
  }> = new EventEmitter<{
    workoutId: string;
    tiers: PlannedTier[];
  }>();
  @Output() workoutNameUpdated: EventEmitter<{
    workoutId: string;
    newName: string;
  }> = new EventEmitter<{
    workoutId: string;
    newName: string;
  }>();
  @Output() workoutCompletionTimeUpdated: EventEmitter<{
    workoutId: string;
    completionTime: number;
  }> = new EventEmitter<{
    workoutId: string;
    completionTime: number;
  }>();
  @Output() workoutEstimatedRPEUpdated: EventEmitter<{
    workoutId: string;
    estimatedRPE: number;
  }> = new EventEmitter<{
    workoutId: string;
    estimatedRPE: number;
  }>();

  private dayInvalidSub = new Subscription();
  get selectedTierMetadata() {
    if (this.selectedTier) {
      return {
        totalTiers: this.workout.tiers.length,
        tierNumber: this.workout.tiers.indexOf(this.selectedTier) + 1,
      };
    }

    return undefined;
  }

  get selectedTierTemplate() {
    if (this.workoutType && this.selectedTier) {
      return this.workoutType.tiers[
        this.workout.tiers.indexOf(this.selectedTier)
      ];
    }

    return undefined;
  }

  constructor(
    private formBuilder: FormBuilder,
    private bus: EventBusService,
    private documentService: DocumentService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.dayInvalidSub = this.bus.subscribe('dayInvalid', value => {
      if (!(this.workout.est_rpe > -1)) {
        this.workoutEstimatedRPE.setErrors({ required: true });
        this.workoutEstimatedRPE.markAsTouched();
      }

      if (!(this.workout.est_completion_time_ms > -1)) {
        this.workoutCompletionTime.setErrors({ required: true });
        this.workoutCompletionTime.markAsTouched();
      }

      this.cdr.detectChanges();
    });

    this.bus.subscribe('dayValidated', value => {
      if (this.workout.est_rpe > -1) {
        this.workoutEstimatedRPE.setErrors({ required: true });
        this.workoutEstimatedRPE.markAsUntouched();
      }

      if (this.workout.est_completion_time_ms > -1) {
        this.workoutCompletionTime.markAsUntouched();
      }
    });
  }

  ngOnDestroy(): void {
    this.dayInvalidSub.unsubscribe();
  }

  ngOnChanges() {
    this.workoutName = this.formBuilder.control(this.workout.name, {
      updateOn: 'change',
    });
    this.workoutCompletionTime = this.formBuilder.control(
      this.workout.est_completion_time_ms &&
        this.workout.est_completion_time_ms > 0
        ? this.workout.est_completion_time_ms / 1000 / 60
        : undefined,
      {
        validators: [
          Validators.min(0),
          (control: AbstractControl): { [key: string]: any } | null => {
            return control.value
              ? !isInteger(control.value)
                ? { restrictIntegers: { value: control.value } }
                : null
              : null;
          },
        ],
        updateOn: 'change',
      },
    );
    this.workoutEstimatedRPE = this.formBuilder.control(this.workout.est_rpe, {
      validators: [Validators.min(0), Validators.max(10)],
      updateOn: 'change',
    });

    if (!this.canEdit) {
      this.workoutName.disable();
      this.workoutCompletionTime.disable();
      this.workoutEstimatedRPE.disable();
    }

    this.workoutName.valueChanges.pipe(debounceTime(500)).subscribe(val => {
      this.updateName(val);
    });

    this.workoutCompletionTime.valueChanges
      .pipe(debounceTime(500))
      .subscribe(val => {
        if (
          this.workoutCompletionTime.valid &&
          this.workoutEstimatedRPE.valid
        ) {
          this.bus.publish('dayValidated', true);
        } else {
          this.bus.publish('dayInvalid', false);
        }
        this.updateWorkoutCompletionTime(val);
      });

    this.workoutEstimatedRPE.valueChanges
      .pipe(debounceTime(500))
      .subscribe(val => {
        if (
          this.workoutCompletionTime.valid &&
          this.workoutEstimatedRPE.valid
        ) {
          this.bus.publish('dayValidated', true);
        } else {
          this.bus.publish('dayInvalid', false);
        }
        this.updateEstimatedWorkoutRPE(val);
      });

    if (this.workout && !this.workout.restrict_label_id && this.workout.tiers) {
      if (this.selectedTier) {
        const foundTier = this.workout.tiers.find(
          t => t.id === this.selectedTier.id,
        );
        if (foundTier) {
          this.selectTier(foundTier);
        } else {
          this.selectTier(undefined);
        }
      }
    }

    this.canMoveTiers = true;
    this.templateWorkoutTypes =
      this.documentService.store.exerciseProgram.document.workout_types;
    if (this.workoutType) {
      const matchingTemplate = this.templateWorkoutTypes.find(
        x => x.id === this.workoutType.id,
      );
      if (matchingTemplate != null) {
        this.canMoveTiers = false;
      }
    }
  }

  setValue(name: string, value: any) {
    const ctrl = this.form.get(name);
    if (ctrl) {
      ctrl.setValue(value);
    }
  }

  updateTiers(tiers: PlannedTier[]) {
    tiers = tiers.sort(PlanDocumentUtils.sortTiers);

    this.tiersUpdated.emit({ workoutId: this.workout.id, tiers: tiers });
  }

  updateTier(tier: PlannedTier) {
    const workout = cloneDeep<Workout>(this.workout);
    const index = workout.tiers.findIndex(t => t.id === tier.id);

    if (index > -1) {
      this.tierUpdated.emit({
        workoutId: workout.id,
        index: index,
        tier: tier,
      });
    }
  }

  addTierFromLibrary(tiers: PlannedTier[]) {
    this.addedTierFromLibrary.emit({
      workoutId: this.workout.id,
      tiers: tiers,
    });
  }

  updateName(val: string) {
    const workout = cloneDeep<Workout>(this.workout);
    this.workoutNameUpdated.emit({ workoutId: workout.id, newName: val });
  }

  updateWorkoutCompletionTime(val: number) {
    this.workoutCompletionTimeUpdated.emit({
      workoutId: this.workout.id,
      completionTime: val || -1,
    });
  }

  updateEstimatedWorkoutRPE(val: number) {
    this.workoutEstimatedRPEUpdated.emit({
      workoutId: this.workout.id,
      estimatedRPE: val !== undefined ? val : -1,
    });
  }

  deleteTier(tier: PlannedTier) {
    const workout = cloneDeep<Workout>(this.workout);
    workout.tiers = workout.tiers.filter(t => t.id !== tier.id);
    workout.tiers.forEach((t, index) => {
      t.order = index + 1;
    });

    workout.tiers = workout.tiers.sort(PlanDocumentUtils.sortTiers);

    this.tiersUpdated.emit({ workoutId: workout.id, tiers: workout.tiers });
  }

  nextTier() {
    const index = this.workout.tiers.indexOf(this.selectedTier);
    if (index > -1 && this.workout.tiers.length > index + 1) {
      this.selectTier(this.workout.tiers[index + 1]);
    }
  }

  previousTier() {
    const index = this.workout.tiers.indexOf(this.selectedTier);
    if (index > 0) {
      this.selectTier(this.workout.tiers[index - 1]);
    }
  }

  selectTier(tier: PlannedTier) {
    this.selectedTier = tier;
  }

  /**
   * Emits addTierToLibrary event, with given tier
   * @param tier
   */
  addTierToLibrary(tier: PlannedTier) {
    this.addedTierToLibrary.emit(tier);
  }
}
